import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, FileText, Sparkles, CheckCircle, Settings, X, Edit, Eye, ExternalLink, Hash, Link, DollarSign, Info, Loader2, Clock, XCircle, RefreshCw, Download, FileDown, Bot, Trash2, FileSearch, StopCircle, Cloud, Users, Unlink } from 'lucide-react'
import api from '../lib/api'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import AgentProgressTracker from '../components/AgentProgressTracker'

const Proposals = () => {
  const navigate = useNavigate()
  const [proposals, setProposals] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [tenders, setTenders] = useState([])
  const [showAIModal, setShowAIModal] = useState(false)
  const [selectedProposal, setSelectedProposal] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [reviewingId, setReviewingId] = useState(null)
  const [showLogsModal, setShowLogsModal] = useState(null)
  const [logsData, setLogsData] = useState(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const pollingRef = useRef(null)
  const observerRef = useRef(null)
  const searchTimeoutRef = useRef(null)
  const ITEMS_PER_PAGE = 50
  
  // Link Lead state
  const [showLinkLeadModal, setShowLinkLeadModal] = useState(false)
  const [linkingProposalId, setLinkingProposalId] = useState(null)
  const [leads, setLeads] = useState([])
  const [leadSearchTerm, setLeadSearchTerm] = useState('')
  const [searchingLeads, setSearchingLeads] = useState(false)
  const [linkingLead, setLinkingLead] = useState(false)
  const [leadPage, setLeadPage] = useState(1)
  const [hasMoreLeads, setHasMoreLeads] = useState(false)
  const leadSearchTimeoutRef = useRef(null)

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 400)
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  // Initial load and search changes
  useEffect(() => {
    setPage(1)
    fetchProposals(1, false)
  }, [debouncedSearch])

  // Fetch tenders on mount
  useEffect(() => {
    fetchTenders()
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [])

  // Debounce lead search
  useEffect(() => {
    if (leadSearchTimeoutRef.current) {
      clearTimeout(leadSearchTimeoutRef.current)
    }
    if (leadSearchTerm.trim().length > 0) {
      leadSearchTimeoutRef.current = setTimeout(() => {
        searchLeadsFromZoho(leadSearchTerm, 1)
      }, 400)
    } else {
      setLeads([])
      setHasMoreLeads(false)
    }
    return () => {
      if (leadSearchTimeoutRef.current) {
        clearTimeout(leadSearchTimeoutRef.current)
      }
    }
  }, [leadSearchTerm])

  const searchLeadsFromZoho = async (term, pageNum = 1) => {
    setSearchingLeads(true)
    try {
      const response = await api.get('/crm/zoho/leads/search', {
        params: { q: term, page: pageNum, limit: 20 }
      })
      const data = response.data?.leads || []
      if (pageNum === 1) {
        setLeads(data)
      } else {
        setLeads(prev => [...prev, ...data])
      }
      setHasMoreLeads(response.data?.hasMore || false)
      setLeadPage(pageNum)
    } catch (error) {
      console.error('Failed to search leads:', error)
      toast.error('Failed to search leads from Zoho')
    } finally {
      setSearchingLeads(false)
    }
  }

  const loadMoreLeads = () => {
    if (hasMoreLeads && !searchingLeads) {
      searchLeadsFromZoho(leadSearchTerm, leadPage + 1)
    }
  }

  const handleOpenLinkLeadModal = (proposalId) => {
    setLinkingProposalId(proposalId)
    setShowLinkLeadModal(true)
    setLeadSearchTerm('')
    setLeads([])
  }

  const handleLinkLead = async (lead) => {
    if (!linkingProposalId) return
    setLinkingLead(true)
    try {
      await api.post(`/proposals/${linkingProposalId}/link-lead`, {
        crmId: lead.crmId,
        leadData: {
          name: lead.name,
          email: lead.email,
          company: lead.company,
          phone: lead.phone
        }
      })
      toast.success('Lead linked successfully')
      setShowLinkLeadModal(false)
      setLinkingProposalId(null)
      setLeadSearchTerm('')
      setLeads([])
      fetchProposals(1, false)
    } catch (error) {
      console.error('Failed to link lead:', error)
      toast.error('Failed to link lead')
    } finally {
      setLinkingLead(false)
    }
  }

  const handleUnlinkLead = async (proposalId) => {
    try {
      await api.delete(`/proposals/${proposalId}/link-lead`)
      toast.success('Lead unlinked successfully')
      fetchProposals(1, false)
    } catch (error) {
      console.error('Failed to unlink lead:', error)
      toast.error('Failed to unlink lead')
    }
  }

  // Poll for generating proposals
  useEffect(() => {
    const hasGenerating = proposals.some(p => p.generationStatus === 'pending' || p.generationStatus === 'processing')
    
    if (hasGenerating) {
      pollingRef.current = setInterval(() => fetchProposals(true), 3000)
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current)
    }
    
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [proposals])

  const fetchProposals = async (pageNum = 1, append = false, silent = false) => {
    if (pageNum === 1 && !append && !silent) {
      setLoading(true)
    } else if (pageNum > 1) {
      setLoadingMore(true)
    }
    try {
      const params = {
        search: debouncedSearch || undefined,
        limit: ITEMS_PER_PAGE,
        page: pageNum
      }
      const response = await api.get('/proposals', { params })
      const data = response.data?.proposals || response.data || []
      const pagination = response.data?.pagination || {}
      
      if (append) {
        setProposals(prev => [...prev, ...data])
      } else {
        setProposals(data)
      }
      
      setHasMore(pagination.hasMore || data.length === ITEMS_PER_PAGE)
      setTotalCount(pagination.totalCount || data.length)
      setPage(pageNum)
    } catch (error) {
      console.error('Failed to fetch proposals:', error)
      if (!silent && pageNum === 1) {
        toast.error('Failed to fetch proposals.')
        setProposals([])
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const getGenerationStatusBadge = (proposal) => {
    if (proposal.generationStatus === 'pending') {
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full text-xs"><Clock className="w-3 h-3" />Pending</span>
    }
    if (proposal.generationStatus === 'processing') {
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs"><Loader2 className="w-3 h-3 animate-spin" />Generating</span>
    }
    if (proposal.generationStatus === 'failed' || proposal.status === 'Failed') {
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-xs"><XCircle className="w-3 h-3" />Failed</span>
    }
    return null
  }

  const fetchTenders = async () => {
    try {
      const response = await api.get('/tenders', { params: { limit: 50 } })
      setTenders(response.data || [])
    } catch (error) {
      console.error('Failed to fetch tenders:', error)
      setTenders([])
    }
  }

  const handleViewDetails = async (proposal) => {
    try {
      const response = await api.get(`/proposals/${proposal._id}`)
      setSelectedProposal(response.data)
      setShowDetailsModal(true)
    } catch (error) {
      console.error('Failed to fetch proposal details:', error)
      toast.error('Failed to load proposal details')
    }
  }

  const handleEdit = async (proposal) => {
    try {
      const response = await api.get(`/proposals/${proposal._id}`)
      setSelectedProposal(response.data)
      setShowEditModal(true)
    } catch (error) {
      console.error('Failed to fetch proposal for editing:', error)
      toast.error('Failed to load proposal for editing')
    }
  }

  const handleSaveEdit = async () => {
    try {
      await api.put(`/proposals/${selectedProposal._id}`, selectedProposal)
      toast.success('Proposal updated successfully')
      setShowEditModal(false)
      setSelectedProposal(null)
      fetchProposals()
    } catch (error) {
      console.error('Failed to save proposal:', error)
      toast.error('Failed to save proposal')
    }
  }

  const handleAIReview = async (proposalId) => {
    setReviewingId(proposalId)
    try {
      const response = await api.post(`/proposals/${proposalId}/review`)
      toast.success('AI Review completed!')
      fetchProposals()
      // Show the review results
      setSelectedProposal(response.data.proposal)
      setShowDetailsModal(true)
    } catch (error) {
      console.error('Failed to run AI review:', error)
      toast.error(error.response?.data?.error || 'Failed to run AI review')
    } finally {
      setReviewingId(null)
    }
  }

  const handleExportPDF = async (proposalId) => {
    try {
      toast.loading('Generating PDF...', { id: 'pdf-export' })
      const response = await api.get(`/proposals/${proposalId}/export/pdf`, {
        responseType: 'blob'
      })
      
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Proposal-${proposalId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('PDF exported successfully', { id: 'pdf-export' })
    } catch (err) {
      console.error('PDF export failed:', err)
      toast.error('Failed to export PDF', { id: 'pdf-export' })
    }
  }

  const handleExportDOC = async (proposalId) => {
    try {
      toast.loading('Generating document...', { id: 'doc-export' })
      const response = await api.get(`/proposals/${proposalId}/export/docx`, {
        responseType: 'blob'
      })
      
      const blob = new Blob([response.data], { type: 'application/msword' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Proposal-${proposalId}.doc`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Document exported successfully', { id: 'doc-export' })
    } catch (err) {
      console.error('DOC export failed:', err)
      toast.error('Failed to export document', { id: 'doc-export' })
    }
  }

  // Infinite scroll observer
  const lastProposalRef = useCallback(node => {
    if (loading || loadingMore) return
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchProposals(page + 1, true)
      }
    })
    if (node) observerRef.current.observe(node)
  }, [loading, loadingMore, hasMore, page])

  // Use proposals directly since filtering is done server-side
  const displayProposals = proposals

  const handleRetryGeneration = async (proposalId) => {
    try {
      toast.loading('Restarting proposal generation...', { id: 'retry' })
      await api.post(`/proposals/${proposalId}/retry`)
      toast.success('Proposal generation restarted!', { id: 'retry' })
      fetchProposals()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to retry', { id: 'retry' })
    }
  }

  const handleDeleteProposal = async (proposalId, proposalTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${proposalTitle}"? This action cannot be undone.`)) {
      return
    }
    try {
      toast.loading('Deleting proposal...', { id: 'delete' })
      const response = await api.delete(`/proposals/${proposalId}`)
      if (response.data.archived) {
        toast.success('Proposal archived successfully', { id: 'delete' })
      } else {
        toast.success('Proposal deleted successfully', { id: 'delete' })
      }
      fetchProposals()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete proposal', { id: 'delete' })
    }
  }

  const handleViewLogs = async (proposalId) => {
    try {
      const response = await api.get(`/proposals/${proposalId}/agent-progress`)
      setLogsData(response.data)
      setShowLogsModal(proposalId)
    } catch (err) {
      toast.error('Failed to load agent logs')
    }
  }

  const handleCancelGeneration = async (proposalId) => {
    try {
      toast.loading('Cancelling generation...', { id: 'cancel' })
      await api.post(`/proposals/${proposalId}/cancel`)
      toast.success('Generation cancelled', { id: 'cancel' })
      fetchProposals()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel', { id: 'cancel' })
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-700',
      'AI Generated': 'bg-blue-100 text-blue-700',
      'Under Review': 'bg-yellow-100 text-yellow-700',
      'Approved': 'bg-green-100 text-green-700',
      'Submitted': 'bg-purple-100 text-purple-700',
      'Won': 'bg-green-100 text-green-700',
      'Lost': 'bg-red-100 text-red-700',
      'Failed': 'bg-red-100 text-red-700',
      'Generating': 'bg-blue-100 text-blue-700'
    }
    return colors[status] || 'bg-gray-100 text-gray-700'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Proposals</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage and track your proposal pipeline</p>
        </div>
        <div className="flex gap-3">
          <div className="relative group">
            <button className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700">
              <Sparkles className="w-5 h-5 mr-2" />
              AI Generate
            </button>
            {/* Dropdown */}
            <div className="hidden group-hover:block absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2 min-w-[200px] z-10">
              {tenders.length > 0 ? (
                tenders.slice(0, 10).map((tender) => (
                  <button
                    key={tender._id}
                    onClick={() => navigate('/proposals/generate', { state: { tenderId: tender._id, title: tender.title, organization: tender.organization, description: tender.description } })}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300"
                  >
                    {tender.title.substring(0, 40)}{tender.title.length > 40 ? '...' : ''}
                  </button>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500">No tenders available</div>
              )}
              <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                <button
                  onClick={() => navigate('/proposals/generate')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-blue-600 dark:text-blue-400 font-medium"
                >
                  Generate without tender
                </button>
              </div>
            </div>
          </div>
          <button 
            onClick={() => navigate('/proposals/generate')}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Proposal
          </button>
          <button 
            onClick={() => navigate('/proposals/templates')}
            className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            <Settings className="w-5 h-5 mr-2" />
            Manage Templates
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search proposals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {/* Proposals Grid */}
      <div className="grid grid-cols-1 gap-4">
        {displayProposals.length === 0 && !loading ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {debouncedSearch ? `No proposals found for "${debouncedSearch}"` : 'No proposals found'}
            </p>
          </div>
        ) : (
          displayProposals.map((proposal, index) => (
          <div
            key={proposal._id}
            ref={index === displayProposals.length - 1 ? lastProposalRef : null}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{proposal.title}</h3>
                  {/* Generation Status Badge */}
                  {getGenerationStatusBadge(proposal) || (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(proposal.status)}`}>
                      {proposal.status}
                    </span>
                  )}
                  {proposal.sections?.some(s => s.aiGenerated) && (
                    <span className="flex items-center px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs">
                      <Sparkles className="w-3 h-3 mr-1" />
                      AI Assisted
                    </span>
                  )}
                  {/* AI Verified Badge with Tooltip */}
                  {proposal.aiInsights && (
                    <div className="relative group">
                      <span className="flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs cursor-help">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        AI Verified
                      </span>
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-20 w-64">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg p-3 shadow-lg">
                          <p className="font-medium mb-1">AI Quality Check</p>
                          <p className="text-gray-300">
                            ✓ {proposal.aiInsights.strengths?.length || 0} strengths identified
                          </p>
                          <p className="text-gray-300">
                            ⚠ {proposal.aiInsights.gaps?.length || 0} gaps found
                          </p>
                          {proposal.aiInsights.suggestions?.length > 0 && (
                            <p className="text-gray-300">
                              💡 {proposal.aiInsights.suggestions.length} suggestions
                            </p>
                          )}
                          <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700"></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Badges Row */}
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Budget Badge */}
                  {proposal.budget && proposal.budget.amount > 0 && (
                    <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                      <DollarSign className="w-3 h-3 mr-1" />
                      ₹{proposal.budget.amount >= 10000000 
                        ? `${(proposal.budget.amount / 10000000).toFixed(2)} Cr` 
                        : proposal.budget.amount >= 100000 
                          ? `${(proposal.budget.amount / 100000).toFixed(2)} L`
                          : proposal.budget.amount.toLocaleString()}
                    </span>
                  )}
                  {/* Tender Badge */}
                  {proposal.tender && (
                    <>
                      <span className="inline-flex items-center px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                        <Hash className="w-3 h-3 mr-1" />
                        Bid: {proposal.tender.gemBidId || proposal.tender.tenderNumber || 'N/A'}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/tenders/${proposal.tender._id}`)
                        }}
                        className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200"
                      >
                        <Link className="w-3 h-3 mr-1" />
                        View Tender
                      </button>
                    </>
                  )}
                  {/* Linked Lead Badge */}
                  {proposal.linkedLead ? (
                    <span className="inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-medium">
                      <Users className="w-3 h-3 mr-1" />
                      {proposal.linkedLead.name || 'Linked Lead'}
                      {proposal.linkedLead.crmId && <Cloud className="w-3 h-3 ml-1" />}
                    </span>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenLinkLeadModal(proposal._id)
                      }}
                      className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      <Users className="w-3 h-3 mr-1" />
                      Link Lead
                    </button>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400">Version {proposal.version}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500">{format(new Date(proposal.updatedAt), 'MMM dd, yyyy')}</p>
              </div>
            </div>

            {proposal.executiveSummary && (
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 line-clamp-2">{proposal.executiveSummary}</p>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">Sections:</span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">{proposal.sections?.length || 0}</span>
              </div>
              {proposal.budget && proposal.budget.amount && (
                <div className="text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Budget:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {proposal.budget.currency || 'USD'} {(proposal.budget.amount / 1000000).toFixed(2)}M
                  </span>
                </div>
              )}
              <div className="text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">Created by:</span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">{proposal.createdBy?.name || 'System'}</span>
              </div>
              {proposal.submissionDate && (
                <div className="text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Submitted:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{format(new Date(proposal.submissionDate), 'MMM dd')}</span>
                </div>
              )}
            </div>


            {/* Agent Progress Tracker for generating proposals */}
            {(proposal.generationStatus === 'pending' || proposal.generationStatus === 'processing') && (
              <div className="mb-4">
                <AgentProgressTracker 
                  proposalId={proposal._id} 
                  compact={true}
                  onComplete={() => fetchProposals()}
                />
              </div>
            )}

            {/* Show error message for failed proposals */}
            {proposal.generationStatus === 'failed' && proposal.generationError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">Generation Failed</p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">{proposal.generationError}</p>
                  </div>
                  <button
                    onClick={() => handleRetryGeneration(proposal._id)}
                    className="flex items-center px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Retry
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              {(proposal.generationStatus === 'pending' || proposal.generationStatus === 'processing') ? (
                <>
                  <button
                    onClick={() => setSelectedProposal(proposal)}
                    className="flex items-center px-3 py-1 text-sm border border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/30"
                  >
                    <Bot className="w-4 h-4 mr-1" />
                    View Progress
                  </button>
                  <button
                    onClick={() => handleCancelGeneration(proposal._id)}
                    className="flex items-center px-3 py-1 text-sm border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30"
                    title="Cancel generation"
                  >
                    <StopCircle className="w-4 h-4 mr-1" />
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {/* View Logs button for completed/failed proposals */}
                  {(proposal.status === 'AI Generated' || proposal.generationStatus === 'completed' || proposal.generationStatus === 'failed') && (
                    <button
                      onClick={() => handleViewLogs(proposal._id)}
                      className="flex items-center px-3 py-1 text-sm border border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      title="View agent generation logs"
                    >
                      <FileSearch className="w-4 h-4 mr-1" />
                      Logs
                    </button>
                  )}
                  <button 
                    onClick={() => handleViewDetails(proposal)}
                    className="flex items-center px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Details
                  </button>
                </>
              )}
              {!(proposal.generationStatus === 'pending' || proposal.generationStatus === 'processing') && (
                <>
                <button 
                  onClick={() => handleEdit(proposal)}
                  className="flex items-center px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Quick Edit
                </button>
                <button 
                  onClick={() => window.open(`/proposals/edit/${proposal._id}`, '_blank')}
                  className="flex items-center px-3 py-1 text-sm border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/30"
                  title="Open in full editor with auto-save"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Edit in Editor
                </button>
                <button 
                  onClick={() => handleAIReview(proposal._id)}
                  disabled={reviewingId === proposal._id}
                  className="flex items-center px-3 py-1 text-sm border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
                >
                  <Sparkles className={`w-4 h-4 mr-1 ${reviewingId === proposal._id ? 'animate-spin' : ''}`} />
                  {reviewingId === proposal._id ? 'Reviewing...' : 'AI Review'}
                </button>
                <button 
                  onClick={() => handleExportPDF(proposal._id)}
                  className="flex items-center px-3 py-1 text-sm border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30"
                  title="Export as PDF"
                >
                  <Download className="w-4 h-4 mr-1" />
                  PDF
                </button>
                <button 
                  onClick={() => handleExportDOC(proposal._id)}
                  className="flex items-center px-3 py-1 text-sm border border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                  title="Export as Word Document"
                >
                  <FileDown className="w-4 h-4 mr-1" />
                  DOC
                </button>
                {/* Delete button - shown for non-submitted proposals */}
                {proposal.status !== 'Submitted' && proposal.status !== 'Won' && (
                  <button 
                    onClick={() => handleDeleteProposal(proposal._id, proposal.title)}
                    className="flex items-center px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-red-50 hover:border-red-300 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:border-red-700 dark:hover:text-red-400"
                    title="Delete proposal"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                </>
              )}
            </div>
          </div>
        ))
        )}
        
        {/* Loading more indicator */}
        {loadingMore && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600 mr-2" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Loading more proposals...</span>
          </div>
        )}
        
        {/* Total count */}
        {displayProposals.length > 0 && (
          <div className="text-center py-2 text-sm text-gray-500 dark:text-gray-400">
            Showing {displayProposals.length} of {totalCount} proposals
            {debouncedSearch && <span> matching "{debouncedSearch}"</span>}
          </div>
        )}
      </div>

      {/* View Details Modal */}
      {showDetailsModal && selectedProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{selectedProposal.title}</h2>
              <button
                onClick={() => { setShowDetailsModal(false); setSelectedProposal(null); }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Status and Meta */}
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedProposal.status)}`}>
                  {selectedProposal.status}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">Version {selectedProposal.version}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Updated: {format(new Date(selectedProposal.updatedAt), 'MMM dd, yyyy')}
                </span>
              </div>

              {/* Executive Summary */}
              {selectedProposal.executiveSummary && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Executive Summary</h3>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedProposal.executiveSummary}</p>
                </div>
              )}

              {/* Sections */}
              {selectedProposal.sections?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Sections</h3>
                  <div className="space-y-4">
                    {selectedProposal.sections.map((section, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{section.title}</h4>
                        <div 
                          className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-sm proposal-content"
                          dangerouslySetInnerHTML={{ __html: section.content }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Insights */}
              {selectedProposal.aiInsights && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-4 flex items-center">
                    <Sparkles className="w-5 h-5 mr-2" />
                    AI Review Insights
                  </h3>
                  {selectedProposal.aiInsights.strengths?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-green-700 dark:text-green-400 mb-2">Strengths</h4>
                      <ul className="list-disc list-inside text-sm text-green-800 dark:text-green-300 space-y-1">
                        {selectedProposal.aiInsights.strengths.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                  {selectedProposal.aiInsights.gaps?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">Gaps</h4>
                      <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300 space-y-1">
                        {selectedProposal.aiInsights.gaps.map((g, i) => <li key={i}>{g}</li>)}
                      </ul>
                    </div>
                  )}
                  {selectedProposal.aiInsights.suggestions?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Suggestions</h4>
                      <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-300 space-y-1">
                        {selectedProposal.aiInsights.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => { setShowDetailsModal(false); handleEdit(selectedProposal); }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Edit Proposal
              </button>
              <button
                onClick={() => { setShowDetailsModal(false); setSelectedProposal(null); }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Proposal</h2>
              <button
                onClick={() => { setShowEditModal(false); setSelectedProposal(null); }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  value={selectedProposal.title || ''}
                  onChange={(e) => setSelectedProposal({...selectedProposal, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select
                  value={selectedProposal.status || 'Draft'}
                  onChange={(e) => setSelectedProposal({...selectedProposal, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="Draft">Draft</option>
                  <option value="AI Generated">AI Generated</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Approved">Approved</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Won">Won</option>
                  <option value="Lost">Lost</option>
                </select>
              </div>

              {/* Executive Summary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Executive Summary</label>
                <textarea
                  value={selectedProposal.executiveSummary || ''}
                  onChange={(e) => setSelectedProposal({...selectedProposal, executiveSummary: e.target.value})}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Sections */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sections</label>
                <div className="space-y-4">
                  {selectedProposal.sections?.map((section, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <input
                        type="text"
                        value={section.title || ''}
                        onChange={(e) => {
                          const newSections = [...selectedProposal.sections];
                          newSections[index] = {...newSections[index], title: e.target.value};
                          setSelectedProposal({...selectedProposal, sections: newSections});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg mb-2 font-medium bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100"
                        placeholder="Section Title"
                      />
                      <textarea
                        value={section.content || ''}
                        onChange={(e) => {
                          const newSections = [...selectedProposal.sections];
                          newSections[index] = {...newSections[index], content: e.target.value};
                          setSelectedProposal({...selectedProposal, sections: newSections});
                        }}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100"
                        placeholder="Section Content"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => { setShowEditModal(false); setSelectedProposal(null); }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agent Logs Modal - for viewing logs after generation */}
      {showLogsModal && logsData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Agent Generation Logs
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  View what each agent processed during generation
                </p>
              </div>
              <button
                onClick={() => { setShowLogsModal(null); setLogsData(null); }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {logsData.stages?.map((stage, index) => (
                <div key={stage.id} className={`p-4 rounded-lg border ${
                  stage.status === 'completed' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
                  stage.status === 'failed' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' :
                  'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{stage.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({stage.agent} Agent)</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      stage.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-300' :
                      stage.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-300' :
                      'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                    }`}>
                      {stage.status}
                    </span>
                  </div>
                  {stage.logs ? (
                    <div className="mt-3 space-y-2 text-sm font-mono">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Model:</span>
                        <span className="text-blue-600 dark:text-blue-400 font-semibold">{stage.logs.model || 'N/A'}</span>
                        {stage.logs.duration > 0 && (
                          <span className="text-gray-400 dark:text-gray-500 ml-auto">
                            {(stage.logs.duration / 1000).toFixed(2)}s
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Input:</span>
                        <pre className="mt-1 p-2 bg-white dark:bg-gray-800 rounded text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-xs overflow-x-auto max-h-24 overflow-y-auto">
                          {stage.logs.input || 'No input data'}
                        </pre>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Output:</span>
                        <pre className="mt-1 p-2 bg-white dark:bg-gray-800 rounded text-green-700 dark:text-green-400 whitespace-pre-wrap text-xs overflow-x-auto max-h-24 overflow-y-auto">
                          {stage.logs.output || 'No output data'}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">No logs available for this stage</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Agent Progress Modal */}
      {selectedProposal && (selectedProposal.generationStatus === 'pending' || selectedProposal.generationStatus === 'processing') && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedProposal.title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  AI Agent Workflow Progress
                </p>
              </div>
              <button
                onClick={() => setSelectedProposal(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <AgentProgressTracker 
                proposalId={selectedProposal._id}
                onComplete={() => {
                  fetchProposals()
                  setSelectedProposal(null)
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* AI Generation Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-lg w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">AI Proposal Generator</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Generate a professional proposal using AI based on tender requirements
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Tender
                </label>
                <select className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                  <option>Choose a tender...</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  AI Model
                </label>
                <select 
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  defaultValue="gemini-2.5-flash"
                >
                  <optgroup label="Google Gemini">
                    <option value="gemini-2.5-flash">Gemini 2.5 Flash (Fast)</option>
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                    <option value="gemini-1.5-pro">Gemini 1.5 Pro (Advanced)</option>
                  </optgroup>
                  <optgroup label="OpenAI">
                    <option value="gpt-4o">GPT-4o (Latest)</option>
                    <option value="gpt-4-turbo">GPT-4 Turbo</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fast)</option>
                  </optgroup>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Select the AI model to use for proposal generation
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAIModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700">
                  Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Lead Modal */}
      {showLinkLeadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Link Lead from Zoho CRM</h3>
              <button onClick={() => { setShowLinkLeadModal(false); setLinkingProposalId(null); setLeadSearchTerm(''); setLeads([]); }} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Type to search leads in Zoho CRM..."
                  value={leadSearchTerm}
                  onChange={(e) => setLeadSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  autoFocus
                />
                {searchingLeads && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 min-h-[200px]">
              {searchingLeads && leads.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">Searching Zoho CRM...</span>
                </div>
              ) : leads.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {leadSearchTerm ? 'No leads found matching your search' : 'Start typing to search leads in Zoho CRM'}
                </p>
              ) : (
                <div className="space-y-2">
                  {leads.map((lead, index) => (
                    <div
                      key={lead.crmId || index}
                      onClick={() => !linkingLead && handleLinkLead(lead)}
                      className={`flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${linkingLead ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                          <span className="text-primary-600 dark:text-primary-400 text-sm font-semibold">
                            {lead.name?.charAt(0) || 'L'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{lead.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{lead.company} • {lead.email}</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs">
                        <Cloud className="w-3 h-3 mr-1" /> Zoho
                      </span>
                    </div>
                  ))}
                  
                  {hasMoreLeads && (
                    <button
                      onClick={loadMoreLeads}
                      disabled={searchingLeads}
                      className="w-full py-2 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                    >
                      {searchingLeads ? 'Loading...' : 'Load More'}
                    </button>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-b-xl">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Linking a lead with Zoho CRM ID will create a Deal in Zoho CRM automatically.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Proposals
